import { day, hasValue } from '@vrplatform/utils';
import { Popover } from '@mantine/core';
import { type DateValue, YearPicker } from '@mantine/dates';
import { useEffect, useMemo, useState } from 'react';
import { FilterPill } from './FilterPill';

type BaseProps = {
  label: string;
  numberOfColumns?: number;
  columnsToScroll?: number;
  minDate?: Date;
  maxDate?: Date;
};

type ValueProps =
  | {
      value: [DateValue, DateValue] | undefined | null;
      setValue: (v: [DateValue, DateValue] | undefined | null) => void;
      type: 'range';
    }
  | {
      value: DateValue | undefined | null;
      setValue: (v: DateValue | undefined | null) => void;
      type: 'default';
    };

type Props = BaseProps & ValueProps;

export const YearFilter = ({
  label: defaultLabel,
  value,
  setValue,
  columnsToScroll = 1,
  numberOfColumns = 1,
  type,
  minDate,
  maxDate,
}: Props) => {
  const [input, setInput] = useState(value);
  const [opened, setOpened] = useState(false);

  const isDefault = useMemo<boolean>(() => {
    if (type === 'range') return !value || value.filter(hasValue).length === 0;

    return !value;
  }, [value, type]);

  const onChange = (v: boolean) => {
    setOpened(v);

    if (input !== value) setValue(input as any);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (input !== value) setInput(value);
  }, [value]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const label = useMemo(() => {
    if (isDefault) return defaultLabel;

    if (type === 'range') {
      const i = input as [DateValue, DateValue] | undefined | null;
      const startDate = i?.[0] ? day(i[0]) : null;
      const endDate = i?.[1] ? day(i[1]) : null;

      const isSameYear = startDate?.year() === endDate?.year();

      if (isSameYear) return startDate?.format('YYYY') || '-';

      return [startDate?.format('YYYY'), endDate?.format('YYYY')]
        .filter(hasValue)
        .join(' - ');
    } else {
      const i = input as DateValue | undefined | null;
      if (!i) return defaultLabel;
      return day(i).format('YYYY');
    }
  }, [value, defaultLabel, isDefault, type]);

  return (
    <Popover
      opened={opened}
      onChange={onChange}
      position="bottom-start"
      radius="md"
      shadow="md"
    >
      <Popover.Target>
        <FilterPill
          aria-label={label || defaultLabel}
          opened={opened}
          isDefault={isDefault}
          onClick={() => setOpened((v) => !v)}
          label={label}
          resetFilter={undefined}
        />
      </Popover.Target>
      <Popover.Dropdown>
        <YearPicker
          type={type}
          numberOfColumns={numberOfColumns}
          columnsToScroll={columnsToScroll}
          nextLabel="Next period"
          previousLabel="Previous period"
          value={input || (type === 'range' ? [null, null] : null)}
          onChange={type === 'default' ? (setValue as any) : setInput}
          color="primary"
          minDate={minDate}
          maxDate={maxDate}
          styles={(theme) => ({
            monthsListControl: {
              '&:hover': {
                backgroundColor: theme.colors[theme.primaryColor][0] + 20,
              },
              '&[data-selected]': {
                backgroundColor: theme.colors[theme.primaryColor][6],
                '&:hover': {
                  backgroundColor: theme.colors[theme.primaryColor][7],
                },
              },
            },
          })}
        />
      </Popover.Dropdown>
    </Popover>
  );
};
